import axios from "axios"
import store from "../store"
import _ from "lodash"

import { updateNurseScheduleForDate } from "../features/futureHuddles/futureSchedulesSlice.jsx"

/**
 * Filters the patient schedule array to return only those patient objects that have been marked as edited.
 *
 * This function iterates over each patient in the provided `patientSchedule` array and attempts to
 * locate a matching edited patient within the `editedPatients` array by comparing the `patientId` property.
 *
 * For each patient:
 * - It uses Lodash's `_.find` to search for a corresponding entry in `editedPatients` where the `patientId`
 *   matches that of the current patient.
 * - If a matching edited patient object is found, the function checks all of its properties.
 *   If any property in the edited patient object has a truthy value (indicating the field was edited),
 *   the corresponding patient object is added to the output array.
 * - If no edited patient is found or none of the fields indicate an edit (truthy value), the patient is skipped.
 *
 * @param {Patient[]} patientSchedule - An array of patient objects. Each patient object must include at least a `patientId` property used for identification.
 * @param {EditedPatient[]} editedPatients - An array of objects representing the edit statuses for patients.
 *                                    Each object should include a `patientId` property and additional fields
 *                                    indicating whether specific aspects of the patient data have been edited.
 *
 * @returns {Patient[]} - An array of patient objects from `patientSchedule` that have one or more edit flags set to a truthy value.
 */
export const filterPatientScheduleForEditedPatients = (patientSchedule, editedPatients) => {
	const output = []
	for (const patient of patientSchedule) {
		const editedPatient = _.find(editedPatients, { patientId: patient.patientId })
		if (!editedPatient) {
			continue
		}
		// Check if any of the editedPatient fields indicate an edit (truthy value).
		for (const key in editedPatient) {
			if (key === "patientId") {
				continue
			}
			if (editedPatient[key]) {
				output.push(patient)
				break
			}
		}
		// If none of the editedPatient fields are truthy, continue to the next patient.
		continue
	}
	return output
}

export const filterEditedPatients = (editedPatients) => {
	const output = []
	for (const patient of editedPatients) {
		for (const key in patient) {
			if (key === "patientId") {
				continue
			}
			if (patient[key]) {
				output.push(patient)
				break
			}
		}
		continue
	}
	return output
}

export const filterEditedNurses = (editedNurses) => {
	const output = []
	for (const nurse of editedNurses) {
		for (const key in nurse) {
			if (key === "nurseId") {
				continue
			}
			if (nurse[key]) {
				output.push(nurse)
				break
			}
		}
		continue
	}
	return output
}

/**
 * Filters the nurse schedule array to return only those nurse objects that have been marked as edited.
 *
 * This function iterates over each nurse in the provided `nurseSchedule` array and attempts to locate a matching edited nurse within the `editedNurses` array by comparing the `nurseId` property.
 *
 * For each nurse:
 * - It uses Lodash's `_.find` to search for a corresponding entry in `editedNurses` where the `nurseId` matches that of the current nurse.
 * - If a matching edited nurse object is found, the function checks all of its properties.
 *   If any property in the edited nurse object has a truthy value (indicating the field was edited),
 *   the corresponding nurse object is added to the output array.
 * - If no edited nurse is found or none of the fields indicate an edit (truthy value), the nurse is skipped.
 *
 * @param {Nurse[]} nurseSchedule - An array of nurse objects. Each nurse object must include at least a `nurseId` property for identification.
 * @param {EditedNurse[]} editedNurses - An array of objects representing the edit statuses for nurses.
 *                                  Each object should include a `nurseId` property and additional fields indicating
 *                                  whether specific aspects of the nurse data have been edited.
 *
 * @returns {Nurse[]} - An array of nurse objects from `nurseSchedule` that have one or more edit flags set to a truthy value.
 */
export const filterNurseScheduleForEditedNurses = (nurseSchedule, editedNurses) => {
	const output = []
	for (const nurse of nurseSchedule) {
		const editedNurse = _.find(editedNurses, { nurseId: nurse.nurseId })
		if (!editedNurse) {
			continue
		}
		// Check if any of the editedNurse fields indicate an edit (truthy value).
		for (const key in editedNurse) {
			if (key === "nurseId") {
				continue
			}
			if (editedNurse[key]) {
				output.push(nurse)
				break
			}
		}
		// If none of the editedNurse fields are truthy, continue to the next nurse.
		continue
	}
	return output
}

export const getScheduleFromMongo = async (
	token,
	date,
	dispatch,
	currentState,
	isTomorrowMode,
	sandbox,
	setTomorrowData,
	setOptimizeData,
	logging
) => {
	try {
		if (!sandbox) {
			const pullResponse = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/get-schedule`,
				{
					date,
					id: currentState.documentId,
					currentPatientSchedule: filterPatientScheduleForEditedPatients(
						currentState.patientSchedule,
						currentState.editedPatients
					),
					currentNurseSchedule: filterNurseScheduleForEditedNurses(
						currentState.nurseSchedule,
						currentState.editedNurses
					),
					editedPatients: filterEditedPatients(currentState.editedPatients),
					editedNurses: filterEditedNurses(currentState.editedNurses)
				},
				{ headers: { Authorization: `Bearer ${token}` } }
			)

			const mergedData = pullResponse.data.payload

			if (isTomorrowMode) {
				logging.info("setting tomorrow data")
				dispatch(setTomorrowData(mergedData))
			} else {
				logging.info("setting optimize data")
				dispatch(setOptimizeData(mergedData))
			}

			return mergedData
		}
	} catch (error) {
		logging.error("Error fetching merged schedule:", error)
	}
}

export const getNurseScheduleFromMongo = async (token, date, logging) => {
	try {
		const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/staffing`, {
			params: { date: date },
			headers: {
				Authorization: `Bearer ${token}`
			}
		})

		const nurseSchedule = response.data.payload.nurseSchedule
		const chargeNurse = response.data.payload.chargeNurse
		logging.info("getNurseScheduleFromMongo - Nurse schedule fetched from MongoDB")

		return { nurseSchedule, chargeNurse }
	} catch (error) {
		logging.error("Error fetching nurse schedule:", error)
	}

	return { nurseSchedule: [], chargeNurse: null }
}

export const updateNurseScheduleInMongo = async (
	token,
	date,
	setNotification,
	isTomorrowMode,
	sandbox,
	floorNurses,
	chargeNurse,
	logging
) => {
	if (!sandbox || !isTomorrowMode) {
		// Push changes to backend
		try {
			const requestBody = {
				nurseSchedule: floorNurses,
				chargeNurse: chargeNurse,
				date: date
			}

			await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/update-schedule`,
				{
					...requestBody
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json"
					}
				}
			)
			logging.info("updateNurseScheduleInMongo - Nurse schedule updated in MongoDB")
			
			store.dispatch(updateNurseScheduleForDate({ 
				date: date, 
				nurses: floorNurses 
			}))
			logging.info("updateNurseScheduleInMongo - Future schedules store updated for date:", date)
		} catch (error) {
			logging.error("Error saving schedule:", error, error.stack)
		}
	}

	if (setNotification !== undefined) {
		setNotification({ visible: true, message: "Changes saved.", success: true })
		setTimeout(() => {
			setNotification({ visible: false })
		}, 2000)
	}
}
